import {
  getFormattedDate,
  getLocaleCurrencyStringFromString,
} from '../utils'
import { type EmployerInvoiceType } from '../utils/employer_invoice_utils'
import QuestionMarkTooltip from './reusable/QuestionMarkTooltip'
import { SkeletonItem } from './reusable/SkeletonItem'

/*
 ****************** Tooltips ******************
 */

const TotalCostTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '18rem' }}
        className='text-xs p-2 w-60 border rounded-md text-center'
      >
        This is the amount you&apos;ll see debited from your bank account.
      </div>
    </QuestionMarkTooltip>
  )
}

const ScheduledPaymentsTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '18rem' }}
        className='text-xs p-2 w-60 border rounded-md text-center'
      >
        This is the scheduled amount spent on insurance premiums, including
        medical, dental and vision coverage.
      </div>
    </QuestionMarkTooltip>
  )
}

export const CoverageCorrectionsTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '22rem' }}
        className='text-xs p-2 w-60 border rounded-md text-center'
      >
        Our understanding of this employee&apos;s monthly cost of coverage for
        prior periods has changed since we last invoiced you. This amount
        reflects any underbilling or overbilling that resulted from our prior
        cost of coverage expectations.
      </div>
    </QuestionMarkTooltip>
  )
}

const OtherTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '22rem' }}
        className='text-xs p-2 w-60 border rounded-md text-center'
      >
        This is the amount spent on other expenses, including QME reimbursements
        and other expenses made using the Thatch provided credit card.
      </div>
    </QuestionMarkTooltip>
  )
}

const PlanYearsToolTip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '22rem' }}
        className='text-xs p-2 w-60 border rounded-md text-center'
      >
        The plan year(s) for which the expenses are being billed. Around year boundaries, you may see expenses from two plan years.
      </div>
    </QuestionMarkTooltip>
  )
}

const EmployerInvoiceBillingSummary = ({
  employerInvoiceData,
  isLoading,
}: {
  employerInvoiceData: EmployerInvoiceType;
  isLoading: boolean;
}) => {
  // These values are not displayed when loading.
  const startDateUTC = employerInvoiceData?.period_start_date
    ? getFormattedDate(employerInvoiceData?.period_start_date, { timeZone: 'UTC' })
    : 'NA'
  const endDateUTC = employerInvoiceData?.period_end_date
    ? getFormattedDate(employerInvoiceData?.period_end_date, { timeZone: 'UTC' })
    : 'NA'

  const planYears = employerInvoiceData?.plan_years || []

  return (
    <div>
      <div className='mb-4'>
        <div className='h3 mb-2'>
          {isLoading
            ? (
              <SkeletonItem widthClass='w-1/5' />
              )
            : (
            `${startDateUTC} - ${endDateUTC}`
              )}
        </div>
        <div className='h1'>Billing Statement</div>
      </div>
      <div className='flex flex-start'>
        <div className='pr-4'>
          <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
            <div>Total</div>
            <div className='ml-2'>
              <TotalCostTooltip />
            </div>
          </div>
          <div data-testid='total-amount'>
            {isLoading
              ? (
                <SkeletonItem widthClass='w-24' />
                )
              : (
                  getLocaleCurrencyStringFromString(
                    employerInvoiceData?.total.value
                  )
                )}
          </div>
        </div>
        <div className='px-4'>
          <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
            <div>Scheduled Payments</div>
            <div className='ml-2'>
              <ScheduledPaymentsTooltip />
            </div>
          </div>
          <div>
            {isLoading
              ? (
                <SkeletonItem widthClass='w-24' />
                )
              : (
                  getLocaleCurrencyStringFromString(
                    employerInvoiceData.amounts.insurance_premiums_total.value
                  )
                )}
          </div>
        </div>
        <div className='px-4'>
          <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
            <div>Coverage Corrections</div>
            <div className='ml-2'>
              <CoverageCorrectionsTooltip />
            </div>
          </div>
          <div>
            {isLoading
              ? (
                <SkeletonItem widthClass='w-24' />
                )
              : (
                  getLocaleCurrencyStringFromString(
                    employerInvoiceData.amounts.coverage_corrections.value
                  )
                )}
          </div>
        </div>
        <div className='px-4'>
          <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
            <div>Other</div>
            <div className='ml-2'>
              <OtherTooltip />
            </div>
          </div>
          <div>
            {isLoading
              ? (
                <SkeletonItem widthClass='w-24' />
                )
              : (
                  getLocaleCurrencyStringFromString(
                    employerInvoiceData.amounts.other.value
                  )
                )}
          </div>
        </div>
        {planYears.length === 1 && (
          <div className='px-4'>
            <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
              <div>Plan Year</div>
              <div className='ml-2'>
                <PlanYearsToolTip />
              </div>
            </div>
            <div>
              {isLoading
                ? (
                  <SkeletonItem widthClass='w-24' />
                  )
                : (
                    planYears[0]
                  )}
            </div>
          </div>)}
        {planYears.length > 1 && (
          <div className='px-4'>
            <div className='flex align-center text-slate-400 font-normal text-xs mb-2'>
              <div>Plan Years</div>
              <div className='ml-2'>
                <PlanYearsToolTip />
              </div>
            </div>
            <div>
              {isLoading
                ? (
                  <SkeletonItem widthClass='w-24' />
                  )
                : (
                    planYears.join(', ')
                  )}
            </div>
          </div>)}
      </div>
    </div>
  )
}

export default EmployerInvoiceBillingSummary
